import { defineComponent } from 'vue'
import {
    loadStripe,
    StripeCardElement,
    SetupIntent,
    PaymentMethod,
    type Stripe,
    StripeElements,
    StripeCardElementChangeEvent,
    SetupIntentResult,
} from '@stripe/stripe-js'
import { successToast, dangerToast } from '../helpers/toasts'
import { Card } from '../store/modules/CardsModule'
import { cardsModule } from '../utils/store-accessor'

interface Data {
    stripePromise: Promise<Stripe | null> | null
}

export default defineComponent({
    name: 'WithPaymentCards',
    data(): Data {
        return {
            stripePromise: null,
        }
    },
    computed: {
        cardsModuleError(): string | null {
            return cardsModule.error
        },
        paymentCardsisLoading(): boolean {
            return cardsModule.isLoading
        },
        stripeInstance(): Stripe | undefined {
            return cardsModule.stripeInstance
        },
        cards(): Card[] {
            return cardsModule.cards
        },
        defaultCardId(): string | undefined {
            return cardsModule.defaultCardId
        },
        hasValidCardInfo(): boolean {
            return cardsModule.hasValidCardInfo
        },
        intent(): SetupIntent | null {
            return cardsModule.intent
        },
        intentResponse(): SetupIntentResult | null {
            return cardsModule.intentResponse
        },
        currentCardId(): string | PaymentMethod | null {
            return cardsModule.currentCardId
        },
        cardError(): string | null | undefined {
            return cardsModule.cardError
        },
        stripeCardElement(): StripeCardElement | null {
            return cardsModule.stripeCardElement
        },
        shouldShowCardForm(): boolean {
            return cardsModule.showCardForm
        },
        isPaymentMethodComplete(): boolean {
            return cardsModule.isPaymentMethodComplete
        },
    },
    methods: {
        setIsLoading(isLoading: boolean) {
            cardsModule.setIsLoading(isLoading)
        },
        setCurrentCardId(card: string) {
            cardsModule.setCurrentCardId(card)
        },
        setIntent(intent: SetupIntent | null) {
            cardsModule.setIntent(intent)
        },
        setIntentResponse(intentResponse: SetupIntentResult | null) {
            cardsModule.setIntentResponse(intentResponse)
        },
        setShowCardForm(show: boolean) {
            cardsModule.setShowCardForm(show)
        },
        setCardError(cardError: string | null) {
            cardsModule.setCardError(cardError)
        },
        setStripeInstance(stripe: Stripe) {
            cardsModule.setStripeInstance(stripe)
        },
        setIsPaymentMethodComplete(isPaymentMethodComplete: boolean) {
            cardsModule.setIsPaymentMethodComplete(isPaymentMethodComplete)
        },
        setPaymentCardElement(element: StripeCardElement | null) {
            cardsModule.setStripeCardElement(element)
        },
        setCards(cards: Card[]) {
            cardsModule.setCards(cards)
        },
        // TODO: Move this to a separate Stripe plugin
        async loadStripeInstance(): Promise<Stripe | null> {
            if (!this.stripePromise) {
                const key = import.meta.env.VITE_STRIPE_PUBLIC_KEY

                this.stripePromise = loadStripe(key)
            }

            const stripe: Stripe | null = await this.stripePromise

            if (stripe) {
                this.setStripeInstance(stripe)
            }

            return stripe
        },
        async obtainIntent() {
            await cardsModule.obtainIntent()

            if (this.cardsModuleError) {
                this.$bvToast.toast('Unable to process cards at this time', dangerToast)
            }
        },
        async getPaymentCards() {
            const localStoragePaymentCards = localStorage.getItem('user.cards')

            if (localStoragePaymentCards) {
                this.getPaymentCardsFromLocalStorage()
            } else {
                await this.getPaymentCardsDirectly()
            }
        },
        async getPaymentCardsFromLocalStorage() {
            const localStoragePaymentCards = localStorage.getItem('user.cards')

            if (localStoragePaymentCards) {
                try {
                    const cards: Card[] = JSON.parse(localStoragePaymentCards)
                    this.setCards(cards)
                } catch (error) {
                    this.clearPaymentCardsInLocalStorage()
                }
            }
        },
        async getPaymentCardsDirectly() {
            this.clearPaymentCardsInLocalStorage()

            await cardsModule.getPaymentCards()

            this.refreshPaymentCardsInLocalStorage()

            if (this.cardsModuleError) {
                this.$bvToast.toast('There was an error obtaining your cards on file', dangerToast)
            }
        },
        async confirmCardSetup() {
            await cardsModule.confirmCardSetup()

            if (this.cardsModuleError) {
                this.$bvToast.toast(
                    'We are currently unable to process cards due to an issue with our card provider. Please try again soon.',
                    dangerToast,
                )
            }
        },
        async addPaymentCard() {
            await cardsModule.addPaymentCard()

            if (this.cardsModuleError) {
                this.$bvToast.toast(this.cardsModuleError, dangerToast)
            }

            this.resetPaymentMethodInput()

            await this.getPaymentCardsDirectly()
        },
        async removePaymentCard(cardId: string) {
            await cardsModule.removePaymentCard(cardId)

            if (this.cardsModuleError) {
                this.$bvToast.toast(this.cardsModuleError, dangerToast)
            } else {
                this.$bvToast.toast('The card has been successfully removed', successToast)
            }

            await this.getPaymentCardsDirectly()
        },
        async setDefaultPaymentCard(cardId: string) {
            await cardsModule.setDefaultPaymentCard(cardId)

            if (this.cardsModuleError) {
                this.$bvToast.toast(this.cardsModuleError, dangerToast)
            } else {
                this.$bvToast.toast('Default payment method has been updated successfully.', successToast)
            }

            await this.getPaymentCardsDirectly()
        },
        async createStripeInput(elem: HTMLElement) {
            if (elem.classList.contains('StripeElement')) return

            try {
                const stripe: Stripe | null = await this.loadStripeInstance()

                if (!stripe) {
                    this.$bvToast.toast('Unable to load payment provider. Please try again later.', dangerToast)
                    return
                }

                const elements: StripeElements = stripe.elements({
                    fonts: [
                        {
                            cssSrc: 'https://fonts.googleapis.com/css?family=Open+Sans:400',
                        },
                    ],
                })

                const stripeCardElement: StripeCardElement = elements.create('card', {
                    style: {
                        base: {
                            color: '#495057',
                            fontFamily: '"Open Sans", "Helvetica Neue", Helvetica, Arial, Verdana, sans-serif',
                            fontSmoothing: 'antialiased',
                            fontSize: '16px',
                            '::placeholder': {
                                color: '#6c757d',
                            },
                        },
                        invalid: {
                            color: '#dc3545',
                            iconColor: '#dc3545',
                        },
                    },
                })

                stripeCardElement.mount(elem)

                stripeCardElement.on('ready', () => {
                    stripeCardElement.focus()
                })

                // Handle real-time validation errors from the card Element.
                stripeCardElement.on('change', (event: StripeCardElementChangeEvent) => {
                    if (this.cards.length && this.currentCardId != 'NEW') {
                        this.setCurrentCardId('NEW')
                    }

                    if (event.error) {
                        this.setCardError(event.error.message)
                        this.setIsPaymentMethodComplete(false)
                    } else {
                        this.setCardError(null)
                    }

                    if (event.complete) {
                        this.setIsPaymentMethodComplete(true)

                        document.getElementById('submitCard')?.focus()
                    } else {
                        this.setIsPaymentMethodComplete(false)
                    }
                })

                this.setPaymentCardElement(stripeCardElement)
            } catch {
                this.$bvToast.toast(
                    'We are currently unable to process cards due to an issue with our card provider. Please try again soon.',
                    dangerToast,
                )
            }
        },
        resetPaymentMethodInput() {
            this.setIntent(null)
            this.setIntentResponse(null)
            this.setShowCardForm(false)
            this.stripeCardElement?.unmount()
            this.setPaymentCardElement(null)
        },
        clearPaymentCardsInLocalStorage() {
            localStorage.removeItem('user.cards')
        },
        refreshPaymentCardsInLocalStorage() {
            const cards = this.cards

            if (cards) {
                localStorage.setItem('user.cards', JSON.stringify(cards))
            }
        },
    },
})
